import { FLAGS } from '@iheartjane/feature-flags';
import { CategoryTabs } from '@iheartjane/menu';
import type { LabeledOption, MenuTab } from '@iheartjane/types';
import { useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';

import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { trackMenuTap } from '@jane/bloom/services/tracking/track';

import styles from './categorySelector.module.css';

const CATEGORY_TO_SLUG: Partial<Record<MenuTab, string>> = {
  'all-products': 'shop-all',
  brand_specials: 'jane-gold',
  specials: 'deals',
};

const SLUG_TO_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_TO_SLUG).map(([k, v]) => [v, k])
);

// Maps URL segments to their parent category for pages that aren't
// direct category routes (e.g. /rows/68/sale → featured).
const SEGMENT_TO_CATEGORY: Record<string, string> = {
  ...SLUG_TO_CATEGORY,
  rows: 'featured',
};

type CategorySelectorProps = {
  displayCategories: LabeledOption[];
};

export const CategorySelector = ({
  displayCategories,
}: CategorySelectorProps) => {
  const generatePath = useFullDestination();
  const {
    categoryImages,
    featureFlags,
    pinnedStore,
    shoppingPreferences,
    theme,
  } = useRootLoaderData();

  const useCustomCategoryImages =
    featureFlags?.[FLAGS.c2CustomCategoryImages] ?? false;
  const primaryColor = theme?.bloom?.colors?.primary?.main ?? '#0E0E0E';

  // Prefer the in-flight navigation target over the current location so the
  // highlight updates optimistically during a category change.
  const { pathname: currentPathname } = useLocation();
  const { location: navigationLocation } = useNavigation();
  const activePathname = navigationLocation?.pathname ?? currentPathname;

  const selectedCategory = useMemo(() => {
    const categoryValues = new Set(displayCategories.map((c) => c.value));
    const segments = activePathname?.split('/').filter(Boolean);

    for (const segment of segments || []) {
      const mapped = SEGMENT_TO_CATEGORY[segment] ?? segment;
      if (categoryValues.has(mapped)) return mapped;
    }

    return undefined;
  }, [activePathname, displayCategories]);

  const defaultCategory = displayCategories[0]?.value;

  const customLabels = useMemo(
    () =>
      displayCategories.reduce<Record<string, string>>((acc, category) => {
        acc[category.value] = category.label;
        return acc;
      }, {}),
    [displayCategories]
  );

  const getUrlString = (category: MenuTab) =>
    generatePath(`/${CATEGORY_TO_SLUG[category] ?? category}`);

  const handleClick = (category: MenuTab) => {
    trackMenuTap(
      {
        defaultOption: defaultCategory,
        menuOptionSelected: category,
        reservationMode: shoppingPreferences?.reservationMode,
      },
      featureFlags
    );
  };

  return (
    <nav className={styles['categorySelector']} id="category-selector">
      <div className={styles['categoryTabsWrapper']}>
        <CategoryTabs
          categories={displayCategories.map(({ value }) => value as MenuTab)}
          customImages={useCustomCategoryImages ? categoryImages : undefined}
          customLabels={customLabels}
          getUrlString={getUrlString}
          onClick={handleClick}
          primaryColor={primaryColor}
          storeState={pinnedStore?.state}
          variant={theme?.bloom?.category_tabs?.style ?? 'icon'}
          selectedCategory={(selectedCategory ?? defaultCategory) as MenuTab}
        />
      </div>
    </nav>
  );
};
